/** Capture `beforeinstallprompt` and trigger the browser install sheet. */

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
};

let deferredInstall: BeforeInstallPromptEvent | null = null;

const PWA_HINT_DISMISSED_KEY = 'esan_paquetes_pwa_hint_dismissed';
const IOS_LAST_OPEN_KEY = 'esan_paquetes_ios_last_open_at';
const IOS_WEEKLY_REMINDER_DISMISSED_KEY = 'esan_paquetes_ios_weekly_reminder_dismissed';
const IOS_ITP_IDLE_MS = 6 * 24 * 60 * 60 * 1000;

export type PwaInstallGuidanceKind = 'chromium-prompt' | 'ios-safari' | 'ios-non-safari' | 'browser-tab';

/** Capacitor native shell (APK/IPA) — skip browser PWA install UX. */
export function isCapacitorNativeApk(): boolean {
  const cap = (globalThis as { Capacitor?: { isNativePlatform?: () => boolean } }).Capacitor;
  return cap?.isNativePlatform?.() === true;
}

export function isPwaInstalledStandalone(): boolean {
  if (globalThis.matchMedia?.('(display-mode: standalone)').matches) return true;
  return (globalThis.navigator as { standalone?: boolean }).standalone === true;
}

export function isPwaInstallEnabled(): boolean {
  if (isCapacitorNativeApk()) return false;
  return (import.meta.env.ADMIN_OFFLINE_SERVICE_WORKER ?? '1') !== '0';
}

export function isIosDevice(): boolean {
  const ua = globalThis.navigator?.userAgent ?? '';
  const platform = globalThis.navigator?.platform ?? '';
  const ipadOs = platform === 'MacIntel' && (globalThis.navigator?.maxTouchPoints ?? 0) > 1;
  return /iPad|iPhone|iPod/.test(ua) || ipadOs;
}

export function isIosNonSafariBrowser(): boolean {
  if (!isIosDevice()) return false;
  const ua = globalThis.navigator?.userAgent ?? '';
  return /CriOS|FxiOS|EdgiOS|OPiOS/.test(ua);
}

/** iOS browser tab / in-app webview — file/camera picker often blocked. */
export function shouldWarnIosPhotoCapture(): boolean {
  if (isCapacitorNativeApk() || !isIosDevice()) return false;
  return !isPwaInstalledStandalone();
}

export function iosPhotoCaptureWarningMessage(): string {
  if (isIosNonSafariBrowser()) {
    return 'En iPhone, la cámara y las fotos pueden fallar aquí. Pulse Compartir → Abrir en Safari, instale la app (Añadir a pantalla de inicio) y ábrala desde el icono.';
  }
  return 'En iPhone, tome fotos desde la app instalada (icono en inicio). Los enlaces de WhatsApp o pestañas del navegador pueden bloquear la cámara.';
}

export function canOfferPwaInstall(): boolean {
  return isPwaInstallEnabled() && deferredInstall != null && !isPwaInstalledStandalone();
}

/** Install guidance when the app is open in a browser tab instead of an installed PWA. */
export function getPwaInstallGuidanceKind(): PwaInstallGuidanceKind | null {
  if (isCapacitorNativeApk()) return null;
  if (!isPwaInstallEnabled() || isPwaInstalledStandalone()) return null;
  if (canOfferPwaInstall()) return 'chromium-prompt';
  if (isIosDevice()) return isIosNonSafariBrowser() ? 'ios-non-safari' : 'ios-safari';
  return 'browser-tab';
}

export function pwaInstallGuidanceTitle(kind: PwaInstallGuidanceKind): string {
  if (kind === 'ios-safari') return 'Instale la app en este dispositivo';
  if (kind === 'ios-non-safari') return 'Abra en Safari para instalar';
  if (kind === 'browser-tab') return 'Instale la app para trabajar offline';
  return 'Instale la app';
}

export function pwaInstallGuidanceSteps(kind: PwaInstallGuidanceKind): readonly string[] {
  if (kind === 'ios-safari') {
    return [
      'Use Safari (no Chrome ni Firefox en iPhone).',
      'Pulse Compartir (↑) y elija Añadir a pantalla de inicio.',
      'Abra la app solo desde el icono, no desde una pestaña.',
      'La primera vez, hágalo con conexión para descargar catálogos.',
    ];
  }
  if (kind === 'ios-non-safari') {
    return [
      'Pulse Compartir y elija Abrir en Safari.',
      'En Safari: Compartir → Añadir a pantalla de inicio.',
      'Abra la app solo desde el icono instalado.',
    ];
  }
  if (kind === 'browser-tab') {
    return [
      'En Chrome o Edge: use Instalar en la barra de dirección o el menú del navegador.',
      'Abra la app solo desde el icono instalado, no desde una pestaña.',
      'La primera vez, hágalo con conexión para descargar catálogos.',
    ];
  }
  return ['Pulse Instalar app y confirme en el navegador.'];
}

export function isPwaStabilityHintDismissed(): boolean {
  try {
    return sessionStorage.getItem(PWA_HINT_DISMISSED_KEY) === '1';
  } catch {
    return false;
  }
}

export function dismissPwaStabilityHint(): void {
  try {
    sessionStorage.setItem(PWA_HINT_DISMISSED_KEY, '1');
  } catch {
    /* ignore */
  }
}

export function shouldShowPwaStabilityHint(): boolean {
  return getPwaInstallGuidanceKind() != null && !isPwaStabilityHintDismissed();
}

function readIosLastOpenAt(): number | null {
  try {
    const raw = localStorage.getItem(IOS_LAST_OPEN_KEY);
    if (!raw) return null;
    const ms = Number.parseInt(raw, 10);
    return Number.isFinite(ms) ? ms : null;
  } catch {
    return null;
  }
}

export function isIosWeeklyReminderDismissed(): boolean {
  try {
    return sessionStorage.getItem(IOS_WEEKLY_REMINDER_DISMISSED_KEY) === '1';
  } catch {
    return false;
  }
}

export function dismissIosWeeklyReminder(): void {
  try {
    sessionStorage.setItem(IOS_WEEKLY_REMINDER_DISMISSED_KEY, '1');
  } catch {
    /* ignore */
  }
}

/** Records iOS PWA open time; returns whether to show the weekly ITP reminder. */
export function recordIosPwaOpenAt(now = Date.now()): 'remind' | 'ok' {
  if (isCapacitorNativeApk()) return 'ok';
  if (!isIosDevice() || !isPwaInstalledStandalone()) return 'ok';
  const last = readIosLastOpenAt();
  const remind = last != null && now - last >= IOS_ITP_IDLE_MS && !isIosWeeklyReminderDismissed();
  try {
    localStorage.setItem(IOS_LAST_OPEN_KEY, String(now));
  } catch {
    /* ignore */
  }
  return remind ? 'remind' : 'ok';
}

/** @internal test hook */
export function readIosLastOpenAtForTests(): number | null {
  return readIosLastOpenAt();
}

/** Listen for install prompt; call `onAvailable` when the login install button may show. */
export function bindShellPwaInstallPrompt(onAvailable?: () => void): () => void {
  if (!isPwaInstallEnabled() || isPwaInstalledStandalone()) return () => {};
  const handler = (event: Event) => {
    event.preventDefault();
    deferredInstall = event as BeforeInstallPromptEvent;
    onAvailable?.();
  };
  globalThis.addEventListener('beforeinstallprompt', handler);
  return () => {
    globalThis.removeEventListener('beforeinstallprompt', handler);
    deferredInstall = null;
  };
}

export async function triggerShellPwaInstall(): Promise<'accepted' | 'dismissed' | 'unavailable'> {
  const event = deferredInstall;
  if (!event) return 'unavailable';
  deferredInstall = null;
  await event.prompt();
  const { outcome } = await event.userChoice;
  return outcome;
}
